<template>
  <div>
    <div
      v-for="field in editedEvaluation.fields"
      :key="field.id"
      class="field-container"
      :class="{'no-border': field.field.type == Consts.FIELD_TYPES.SEPARATOR}"
    >
      <div
        v-if="field.field.type != Consts.FIELD_TYPES.SEPARATOR"
        class="grip"
      />
      <div
        v-if="field.field.type != Consts.FIELD_TYPES.SEPARATOR"
        class="field"
      >
        <div>
          <div class="input">
            <template v-if="field.field.type == Consts.FIELD_TYPES.SIMPLE_TEXT">
              <v-text-field
                v-model.lazy="field[Consts.EVAL_TARGETS[field.targeted_col]]"
                :label="field.field.name"
                outline
                :rules="[v => !!v || !field.field.required || 'This field is required']"
                :readonly="readonly"
                @change="change"
              />
            </template>
            <template v-if="field.field.type == Consts.FIELD_TYPES.LONG_TEXT">
              <v-textarea
                v-model.lazy="field[Consts.EVAL_TARGETS[field.targeted_col]]"
                :label="field.field.name"
                outline
                :rules="[v => !!v || !field.field.required || 'This field is required']"
                :readonly="readonly"
                @change="change"
              />
            </template>
            <template v-if="field.field.type == Consts.FIELD_TYPES.DATE">
              <v-text-field
                key="date_txt"
                v-model.lazy="lambda_date_txt[field.id]"
                :label="field.field.name"
                outline
                :rules="[v => !!v || !field.field.required || 'This field is required']"
                :readonly="readonly"
                @blur="field[Consts.EVAL_TARGETS[field.targeted_col]] = parseDate(lambda_date_txt[field.id])"
                @change="change"
              >
                <div slot="prepend-inner">
                  <v-menu
                    :ref="`dateField_${field.id}`"
                    :close-on-content-click="false"
                    :return-value.sync="field[Consts.EVAL_TARGETS[field.targeted_col]]"
                    transition="scale-transition"
                    offset-y
                    full-width
                  >
                    <v-icon slot="activator">
                      event
                    </v-icon>
                    <v-date-picker
                      v-model.lazy="field[Consts.EVAL_TARGETS[field.targeted_col]]"
                      first-day-of-week="1"
                      :readonly="readonly"
                      @input="$refs[`dateField_${field.id}`][0].save(field[Consts.EVAL_TARGETS[field.targeted_col]]); lambda_date_txt[field.id] = americanizeDate(field[Consts.EVAL_TARGETS[field.targeted_col]])"
                      @change="change"
                    />
                  </v-menu>
                </div>
              </v-text-field>
            </template>
            <template v-if="field.field.type == Consts.FIELD_TYPES.EMAIL">
              <v-text-field
                v-model.lazy="field[Consts.EVAL_TARGETS[field.targeted_col]]"
                :label="field.field.name"
                outline
                prepend-inner-icon="email"
                :rules="[
                  v => !!v || !field.field.required || 'This field is required',
                  v => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || 'E-mail must be valid'
                ]"
                :readonly="readonly"
                @change="change"
              />
            </template>
            <template v-if="field.field.type == Consts.FIELD_TYPES.NUMBER">
              <v-text-field
                v-model.lazy="field[Consts.EVAL_TARGETS[field.targeted_col]]"
                :label="field.field.name"
                outline
                prepend-inner-icon="mdi-sort-numeric-variant"
                :rules="[
                  v => !!v || !field.field.required || 'This field is required',
                  v => !v || /^[0-9]+$/.test(String(v).replace(/[.,]/, '').replaceAll(' ', '')) || 'The value of this field must be a number'
                ]"
                :readonly="readonly"
                @change="change"
              />
            </template>
            <template v-if="field.field.type == Consts.FIELD_TYPES.SINGLE_CHOICE">
              <div class="select-container">
                <div class="select-box">
                  <v-select
                    v-model.lazy="field[Consts.EVAL_TARGETS[field.targeted_col]]"
                    :items="field.field.options"
                    item-text="name"
                    item-value="id"
                    :clearable="true"
                    :label="field.field.name"
                    outline
                    :rules="[
                      v => !!v || !field.field.required || 'This field is required',
                    ]"
                    :readonly="readonly"
                    @change="updateSingleChoice(field)"
                  />
                </div>
              </div>
            </template>
            <template v-if="field.field.type == Consts.FIELD_TYPES.MULTIPLE_CHOICES">
              <div class="select-container">
                <div class="select-box">
                  <v-select
                    v-model.lazy="field.options"
                    :items="field.field.options"
                    item-text="name"
                    item-value="id"
                    :clearable="true"
                    :label="field.field.name"
                    outline
                    multiple
                    :rules="[
                      v => v.length > 0 || !field.field.required || 'This field is required',
                    ]"
                    :readonly="readonly"
                    @change="change"
                  />
                </div>
              </div>
            </template>
            <template v-if="field.field.type == Consts.FIELD_TYPES.RADIO_GROUP">
              <div class="label">
                {{ field.field.name }}
              </div>
              <div class="select-container">
                <div class="select-box">
                  <div
                    v-if="field.field.options.length > 0"
                    class="radio-group-container"
                  >
                    <v-radio-group
                      v-model.lazy="field[Consts.EVAL_TARGETS[field.targeted_col]]"
                      :mandatory="!!field.field.required"
                      row
                      :rules="[
                        v => !!v || !field.field.required || 'This field is required',
                      ]"
                      :readonly="readonly"
                      @change="updateSingleChoice(field)"
                    >
                      <v-radio
                        v-for="item in field.field.options"
                        :key="item.id"
                        :value="item.id"
                        :clearable="true"
                        :label="item.name"
                        color="primary"
                      />
                    </v-radio-group>
                    <a
                      :class="{disabled: readonly}"
                      @click="emptyRadioGroup(field)"
                    >Empty !</a>
                  </div>
                </div>
              </div>
            </template>
          </div>
          <div
            v-if="field.field.tip.trim()"
            class="tip"
          >
            <v-icon>mdi-information-slab-box</v-icon>
            <div v-html="field.field.tip" />
          </div>
        </div>
      </div>
      <div
        v-else
        class="separator"
      >
        <h3>{{ field.field.name }}</h3>
        <hr>
      </div>
    </div>
  </div>
</template>

<script>
  import Consts from "@/consts"
  import moment from 'moment'

  export default {
    props: {
      evaluation: {
        type: Object,
        required: true
      },
      readonly: {
        type: Boolean,
        required: false,
        default: false
      }
    },
    data() {
      return {
        Consts,
        editedEvaluation: {},
        lambda_date_txt: {} // pour les valeurs textuelles des champs de type date
      }
    },
    mounted() {
      this.editedEvaluation = structuredClone(this.evaluation)
      this.editedEvaluation.fields.forEach(f => {
        if(f.field.type == Consts.FIELD_TYPES.MULTIPLE_CHOICES) {
          f.options.splice(0, f.options.length, ...f.options.map(o => o.choice_id))
        }
        else if(f.field.type == Consts.FIELD_TYPES.DATE) {
          this.lambda_date_txt[f.id] = this.americanizeDate(f.date_value)
        }
      })
      this.change() //TIPS : c'est pour répercuter les modifs initiales sur les champs réalisées dans le mounted sur le père
    },
    methods: {
      americanizeDate(date) {
        if (!date) return null;

        if (date.indexOf(".") >= 0) {
          return date;
        }

        return moment(date, "YYYY-MM-DD").format("DD.MM.YYYY");
      },
      parseDate(date) {
        if (!date) return null;

        if (date.indexOf("-") >= 0) {
          return date;
        }

        return moment(date, "DD.MM.YYYY").format("YYYY-MM-DD");
      },
      updateSingleChoice(field) { //pour pouvoir store la valeur textuelle aussi
        let opt = null
        if(field.single_choice_id) {
          opt = field.field.options.find(o => o.id == field.single_choice_id)
        }
        field.single_choice_value = opt ? opt.name : null
        this.change() //on finit par informer le parent
      },
      change() {
        this.$emit('change', this.editedEvaluation)
      },
      emptyRadioGroup(field) {
        if(!this.readonly) {
          field[Consts.EVAL_TARGETS[field.targeted_col]] = null
          this.updateSingleChoice(field)
        }
      }
    }
  }
</script>

<style scoped lang="sass">
  .field-container
    border-radius: 5px
    border: 1px solid #F1F1F1
    display: flex
    flex-direction: row

    &.no-border
      border: none !important

    .grip
      width: 3px
      background-color: #f4b944

    .field
      flex-grow: 1
      padding: 10px

      .tip
        font-size: 0.9em
        font-style: italic
        margin-top: -8px
        display: flex
        flex-direction: row
        align-items: flex-start

        > div
          background-color: #F1F1F1
          padding: 10px
          margin-left: 5px
          flex-grow: 1


  .field-container + .field-container
    margin-top: 10px

  .separator
    display: flex
    flex-direction: row
    justify-content: center
    margin: 30px 0px
    width: 100%

    hr
      flex-grow: 1
      align-self: center

    h3
      margin-right: 20px

  .radio-group-container
    display: flex
    flex-direction: row

    > *
      align-self: center

    .v-input--radio-group
      flex-shrink: 1
      flex-grow: 0

    a
      display: inline-block
      margin-bottom: 5px
      margin-left: 10px
      text-decoration: underline

      &.disabled
        color: #AAAAAA
        cursor: default
</style>
